import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import {
  CREATE_INVOICE_MODE_OPTIONS,
  DEFAULT_CREATE_INVOICE_MODE,
  DEFAULT_ENABLED_CREATE_INVOICE_MODES,
} from "../../../../constants/createInvoiceModes";
import {
  DEFAULT_BILLING_CONFIGURATION,
  DEFAULT_INVOICE_EMAIL_SENDER,
  INVOICE_EMAIL_SENDER_MODES,
  normalizeInvoiceEmailSender,
  isValidInvoiceSenderEmail,
  DEFAULT_DEPOSIT_CHECK_CONFIGURATION,
  DEFAULT_QBO_IDENTIFIER_TEMPLATES,
  DEFAULT_SALES_ORDER_SNAPSHOT_CONFIGURATION,
  QBO_IDENTIFIER_TEMPLATE_FIELDS,
  findUnsupportedQboIdentifierTokens,
  normalizeBillingFinancialConfig,
  normalizeDepositCheckFinancialConfig,
  normalizeFinancialConfiguration,
  normalizeSalesOrderSnapshotFinancialConfig,
  normalizeStripePaymentsFinancialConfig,
  resolveQboIdentifierPreview,
  resolveStripePaymentDescriptionPreview,
} from "../../../../constants/financialConfiguration";
import { fetchFinancialConfigurationUsers } from "../../../../services/users";
import {
  buildFinanceRecipientOptions,
  ELIGIBLE_FINANCE_ROLES,
  filterFinanceRecipientOptions,
  normalizeFinanceRecipientIds,
} from "./financialConfigurationUsers";
import { isStripePaymentsTabVisible } from "./financialStripePayments";
import SupplierSearchSelectAsync from "../../../../components/SupplierSearchSelectAsync";

const AUTOCOMPLETE_LISTBOX_STYLE = {
  maxHeight: 320,
  overflowY: "auto",
};

const formatRoleLabel = (role) =>
  String(role || "")
    .trim()
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

export const addInternalSupplierId = (ids = [], supplierId) =>
  Array.from(new Set([...ids, supplierId]
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)));

export const removeInternalSupplierId = (ids = [], supplierId) =>
  ids.filter((id) => Number(id) !== Number(supplierId));

export default function FinancialConfigurationTab({ instanceUniqueId }) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = useMemo(
    () => ({ headers: { accessToken: localStorage.getItem("accessToken") } }),
    []
  );
  const [instanceId, setInstanceId] = useState(null);
  const [enabledModes, setEnabledModes] = useState(DEFAULT_ENABLED_CREATE_INVOICE_MODES);
  const [defaultMode, setDefaultMode] = useState(DEFAULT_CREATE_INVOICE_MODE);
  const [enableInvoiceCreationReview, setEnableInvoiceCreationReview] = useState(false);
  const [billingConfig, setBillingConfig] = useState(DEFAULT_BILLING_CONFIGURATION);
  const [invoiceEmailSender, setInvoiceEmailSender] = useState(DEFAULT_INVOICE_EMAIL_SENDER);
  const [depositCheckConfig, setDepositCheckConfig] = useState(
    DEFAULT_DEPOSIT_CHECK_CONFIGURATION
  );
  const [salesOrderSnapshotConfig, setSalesOrderSnapshotConfig] = useState(
    DEFAULT_SALES_ORDER_SNAPSHOT_CONFIGURATION
  );
  const [decoratorOptions, setDecoratorOptions] = useState([]);
  const [internalSupplierOptions, setInternalSupplierOptions] = useState([]);
  const [supplierSearchKey, setSupplierSearchKey] = useState(0);
  const [financeRecipientOptions, setFinanceRecipientOptions] = useState([]);
  const [financeUsersTotalCount, setFinanceUsersTotalCount] = useState(0);
  const [financeUsersLoading, setFinanceUsersLoading] = useState(false);
  const [financeUsersError, setFinanceUsersError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("general");
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeStatusLoading, setStripeStatusLoading] = useState(true);
  const [stripePaymentsConfig, setStripePaymentsConfig] = useState(
    normalizeStripePaymentsFinancialConfig()
  );
  const [qboStatus, setQboStatus] = useState(null);
  const [qboStatusLoading, setQboStatusLoading] = useState(true);
  const [qboIdentifierTemplates, setQboIdentifierTemplates] = useState({
    invoiceCustomerMemo: "",
    invoicePrivateNote: "",
    billPrivateNote: "",
  });
  const [qboSaving, setQboSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadConfiguration = async () => {
      if (!instanceUniqueId) return;

      setLoading(true);
      setFinanceUsersLoading(true);
      setFinanceUsersError("");
      setStripeStatusLoading(true);
      setQboStatusLoading(true);

      try {
        const instanceRes = await axios.get(
          `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
          headers
        );
        if (cancelled) return;

        const nextInstanceId = instanceRes.data?.id || null;
        setInstanceId(nextInstanceId);
        if (!nextInstanceId) return;

        const [defaultsRes, decoratorsRes, usersResult, stripeSettingsRes, qboStatusRes, qboSettingsRes] = await Promise.all([
          axios.get(`${SERVER}/instances/${nextInstanceId}/defaults`, headers),
          axios.get(`${SERVER}/decorators`, {
            ...headers,
            params: { instanceUniqueId },
          }),
          fetchFinancialConfigurationUsers(instanceUniqueId),
          axios.get(`${SERVER}/instances/${instanceUniqueId}/stripe-settings`, headers)
            .catch(() => ({ data: { status: { usable: false } } })),
          axios.get(`${SERVER}/invoices/qbo/status`, {
            ...headers,
            params: { instanceUniqueId },
          }).catch(() => ({ data: { connected: false, availableConnections: [] } })),
          axios.get(`${SERVER}/invoices/qbo/settings`, {
            ...headers,
            params: { instanceUniqueId },
          }).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;

        const financialConfiguration = normalizeFinancialConfiguration(
          defaultsRes.data?.defaults?.financialConfiguration || {}
        );
        setEnabledModes(financialConfiguration.createInvoice.enabledModes);
        setDefaultMode(financialConfiguration.createInvoice.defaultMode);
        setEnableInvoiceCreationReview(financialConfiguration.createInvoice.enableInvoiceCreationReview);
        setInvoiceEmailSender(financialConfiguration.invoiceEmailSender);
        setBillingConfig(financialConfiguration.billing);
        setDepositCheckConfig(
          normalizeDepositCheckFinancialConfig(financialConfiguration.depositCheck || {})
        );
        setSalesOrderSnapshotConfig(financialConfiguration.salesOrderSnapshot);
        setStripePaymentsConfig(financialConfiguration.stripePayments);
        setStripeStatus(stripeSettingsRes.data?.status || null);
        setQboStatus({
          ...(qboStatusRes.data || {}),
          qbo_export_enabled: qboSettingsRes.data?.qbo_export_enabled === true,
        });
        setQboIdentifierTemplates({
          invoiceCustomerMemo: qboSettingsRes.data?.invoice_customer_memo_template || "",
          invoicePrivateNote: qboSettingsRes.data?.invoice_private_note_template || "",
          billPrivateNote: qboSettingsRes.data?.bill_private_note_template || "",
        });

        const decorators = Array.isArray(decoratorsRes.data?.result)
          ? decoratorsRes.data.result
          : [];
        setDecoratorOptions(
          decorators
            .map((decorator) => ({
              id: Number(decorator?.id),
              name:
                String(
                  decorator?.title ||
                    decorator?.Description ||
                    decorator?.name ||
                    decorator?.Name ||
                    ""
                ).trim() || `Decorator ${decorator?.id}`,
            }))
            .filter((option) => Number.isInteger(option.id))
            .sort((a, b) => a.name.localeCompare(b.name))
        );

        const internalSupplierIds = financialConfiguration.billing.minimalBillEntry.internalSupplierIds;
        if (internalSupplierIds.length) {
          const suppliersRes = await axios.get(
            `${SERVER}/suppliers/core-suppliers/by-ids`,
            {
              ...headers,
              params: {
                instanceUniqueId,
                ids: internalSupplierIds.join(","),
              },
            }
          ).catch((error) => {
            console.error("Unable to hydrate configured internal suppliers", error);
            return { data: { rows: [] } };
          });
          if (cancelled) return;
          setInternalSupplierOptions(
            Array.isArray(suppliersRes.data?.rows) ? suppliersRes.data.rows : []
          );
        } else {
          setInternalSupplierOptions([]);
        }

        const nextFinanceRecipientOptions = buildFinanceRecipientOptions(usersResult.listOfUsers);
        setFinanceRecipientOptions(nextFinanceRecipientOptions);
        setFinanceUsersTotalCount(nextFinanceRecipientOptions.length);
      } catch (error) {
        if (!cancelled) {
          toast.error(error?.response?.data?.message || "Unable to load financial configuration.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFinanceUsersLoading(false);
          setStripeStatusLoading(false);
          setQboStatusLoading(false);
        }
      }
    };

    loadConfiguration();
    return () => {
      cancelled = true;
    };
  }, [SERVER, headers, instanceUniqueId]);

  const enabledOptions = useMemo(
    () => CREATE_INVOICE_MODE_OPTIONS.filter((option) => enabledModes.includes(option.id)),
    [enabledModes]
  );

  const toggleMode = (mode) => {
    setEnabledModes((prev) => {
      const next = prev.includes(mode)
        ? prev.filter((item) => item !== mode)
        : [...prev, mode].filter((item) => DEFAULT_ENABLED_CREATE_INVOICE_MODES.includes(item));
      if (next.length && !next.includes(defaultMode)) {
        setDefaultMode(
          next.includes(DEFAULT_CREATE_INVOICE_MODE) ? DEFAULT_CREATE_INVOICE_MODE : next[0]
        );
      }
      return next;
    });
  };

  const handleMinimalBillEntryEnabledChange = (_, checked) => {
    setBillingConfig((prev) => ({
      ...prev,
      minimalBillEntry: {
        ...prev.minimalBillEntry,
        enabled: checked,
      },
    }));
  };

  const handleInternalDecoratorIdsChange = (event) => {
    const nextIds = Array.isArray(event.target.value)
      ? Array.from(
          new Set(
            event.target.value
              .map((value) => Number(value))
              .filter((value) => Number.isInteger(value) && value > 0)
          )
        )
      : [];

    setBillingConfig((prev) => ({
      ...prev,
      minimalBillEntry: {
        ...prev.minimalBillEntry,
        internalDecoratorIds: nextIds,
      },
    }));
  };

  const handleInternalSupplierSelect = (supplier) => {
    const supplierId = Number(supplier?.id ?? supplier?.Id);
    if (!Number.isInteger(supplierId) || supplierId <= 0) return;

    setInternalSupplierOptions((prev) =>
      prev.some((option) => Number(option?.id ?? option?.Id) === supplierId)
        ? prev
        : [...prev, supplier]
    );
    setBillingConfig((prev) => ({
      ...prev,
      minimalBillEntry: {
        ...prev.minimalBillEntry,
        internalSupplierIds: addInternalSupplierId(
          prev.minimalBillEntry.internalSupplierIds,
          supplierId
        ),
      },
    }));
    setSupplierSearchKey((key) => key + 1);
  };

  const handleInternalSupplierRemove = (supplierId) => {
    setBillingConfig((prev) => ({
      ...prev,
      minimalBillEntry: {
        ...prev.minimalBillEntry,
        internalSupplierIds: removeInternalSupplierId(
          prev.minimalBillEntry.internalSupplierIds,
          supplierId
        ),
      },
    }));
  };

  const handleDepositCheckRecipientIdsChange = (ids) => {
    setDepositCheckConfig((prev) => ({
      ...prev,
      financeRecipientUserIds: normalizeFinanceRecipientIds(ids),
    }));
  };

  const selectedInternalDecoratorIds = billingConfig.minimalBillEntry.internalDecoratorIds;
  const selectedInternalSupplierIds = billingConfig.minimalBillEntry.internalSupplierIds;
  const selectedRecipientIds = depositCheckConfig.financeRecipientUserIds;
  const financeRecipientOptionMap = useMemo(
    () => new Map(financeRecipientOptions.map((option) => [option.id, option])),
    [financeRecipientOptions]
  );
  const selectedFinanceRecipientOptions = useMemo(
    () =>
      selectedRecipientIds
        .map((id) => financeRecipientOptionMap.get(id))
        .filter(Boolean),
    [financeRecipientOptionMap, selectedRecipientIds]
  );
  const invalidSelectedRecipientIds = useMemo(
    () =>
      financeUsersLoading
        ? []
        : selectedRecipientIds.filter((id) => !financeRecipientOptionMap.has(id)),
    [financeRecipientOptionMap, financeUsersLoading, selectedRecipientIds]
  );
  const selectedEligibleRecipientCount = selectedFinanceRecipientOptions.length;
  const invoiceSenderUserOptions = useMemo(() => financeRecipientOptions.filter((option) =>
    ELIGIBLE_FINANCE_ROLES.has(option.normalizedRole) && isValidInvoiceSenderEmail(option.email)
  ), [financeRecipientOptions]);

  const save = async () => {
    if (!instanceId) return;

    const safeEnabledModes = enabledModes.filter(
      (mode, index, list) =>
        DEFAULT_ENABLED_CREATE_INVOICE_MODES.includes(mode) && list.indexOf(mode) === index
    );
    if (!safeEnabledModes.length) {
      toast.error("Enable at least one invoice creation option.");
      return;
    }

    const safeSenderConfig = normalizeInvoiceEmailSender(invoiceEmailSender);
    if (safeSenderConfig.mode === INVOICE_EMAIL_SENDER_MODES.PREFERRED_USER &&
        !invoiceSenderUserOptions.some((option) => option.id === safeSenderConfig.preferredUserId)) {
      toast.error('Select an available finance or administrator user as the preferred invoice sender.');
      return;
    }
    if (safeSenderConfig.mode === INVOICE_EMAIL_SENDER_MODES.CUSTOM_EMAIL &&
        !isValidInvoiceSenderEmail(safeSenderConfig.customEmail)) {
      toast.error('Enter a valid fixed invoice sender address.');
      return;
    }

    const safeDefaultMode = safeEnabledModes.includes(defaultMode)
      ? defaultMode
      : safeEnabledModes.includes(DEFAULT_CREATE_INVOICE_MODE)
        ? DEFAULT_CREATE_INVOICE_MODE
        : safeEnabledModes[0];

    setSaving(true);
    try {
      const response = await axios.put(
        `${SERVER}/instances/${instanceId}/defaults`,
        {
          financialConfiguration: {
            createInvoice: {
              enabledModes: safeEnabledModes,
              defaultMode: safeDefaultMode,
              enableInvoiceCreationReview,
            },
            invoiceEmailSender: safeSenderConfig,
            billing: normalizeBillingFinancialConfig(billingConfig),
            depositCheck: normalizeDepositCheckFinancialConfig(depositCheckConfig),
            salesOrderSnapshot: normalizeSalesOrderSnapshotFinancialConfig(
              salesOrderSnapshotConfig
            ),
            stripePayments: normalizeStripePaymentsFinancialConfig(stripePaymentsConfig),
          },
        },
        headers
      );

      const savedConfig = normalizeFinancialConfiguration(
        response.data?.defaults?.financialConfiguration || {}
      );
      setEnabledModes(savedConfig.createInvoice.enabledModes);
      setDefaultMode(savedConfig.createInvoice.defaultMode);
      setEnableInvoiceCreationReview(savedConfig.createInvoice.enableInvoiceCreationReview);
      setInvoiceEmailSender(savedConfig.invoiceEmailSender);
      setBillingConfig(savedConfig.billing);
      setDepositCheckConfig(savedConfig.depositCheck);
      setSalesOrderSnapshotConfig(savedConfig.salesOrderSnapshot);
      setStripePaymentsConfig(savedConfig.stripePayments);
      toast.success("Financial configuration saved.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save financial configuration.");
    } finally {
      setSaving(false);
    }
  };

  const saveQboIdentifiers = async () => {
    if (!instanceUniqueId) return;

    setQboSaving(true);
    try {
      await axios.put(
        `${SERVER}/invoices/qbo/settings`,
        {
          instanceUniqueId,
          invoice_customer_memo_template: qboIdentifierTemplates.invoiceCustomerMemo,
          invoice_private_note_template: qboIdentifierTemplates.invoicePrivateNote,
          bill_private_note_template: qboIdentifierTemplates.billPrivateNote,
        },
        headers
      );
      const res = await axios.get(`${SERVER}/invoices/qbo/settings`, {
        ...headers,
        params: { instanceUniqueId },
      });
      setQboIdentifierTemplates({
        invoiceCustomerMemo: res.data?.invoice_customer_memo_template || "",
        invoicePrivateNote: res.data?.invoice_private_note_template || "",
        billPrivateNote: res.data?.bill_private_note_template || "",
      });
      toast.success("QuickBooks identifiers saved.");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to save QuickBooks identifiers.");
    } finally {
      setQboSaving(false);
    }
  };

  const financeUsersEmpty =
    !financeUsersLoading && financeRecipientOptions.length === 0 && !financeUsersError;
  const showStripePayments = isStripePaymentsTabVisible({
    loading: stripeStatusLoading,
    status: stripeStatus,
  });
  const showQboIdentifiers =
    qboStatusLoading === false &&
    (qboStatus?.connected === true ||
      qboStatus?.qbo_export_enabled === true ||
      (Array.isArray(qboStatus?.availableConnections) && qboStatus.availableConnections.length > 0));
  const stripeDescriptions = stripePaymentsConfig.native.descriptions;
  const promoStoreDescription = stripePaymentsConfig.promoStores.descriptions.payment;
  const previewContext = {
    orderId: 346,
    invoiceId: 123,
    customerName: "Example Company",
    amount: "500.00",
    currency: "CAD",
    paymentType: "Invoice",
  };
  const qboPreviewContext = {
    orderId: 346,
    invoiceId: 582,
    billId: 901,
    poNumber: "346-1100",
    customerName: "Example Company",
    supplierName: "Example Supplier",
    amount: "500.00",
    currency: "CAD",
  };
  const stripeDescriptionFields = [
    ["invoice", "Standard Invoice"],
    ["deposit", "Deposit"],
    ["balance", "Balance Payment"],
    ["fullUpfront", "Full Upfront Payment"],
    ["partial", "Partial Invoice"],
    ["progress", "Progress Invoice"],
  ];

  useEffect(() => {
    if (!showStripePayments && activeSection === "stripe") setActiveSection("general");
    if (!showQboIdentifiers && activeSection === "qbo") setActiveSection("general");
  }, [activeSection, showQboIdentifiers, showStripePayments]);

  return (
    <Box sx={{ p: 2, display: "grid", gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Financial Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Control invoice, billing, and finance behavior for this instance.
        </Typography>
      </Box>

      <Tabs value={activeSection} onChange={(_, value) => setActiveSection(value)}>
        <Tab value="general" label="General" />
        {showStripePayments ? <Tab value="stripe" label="Stripe Payments" /> : null}
        {showQboIdentifiers ? <Tab value="qbo" label="QuickBooks Online" /> : null}
      </Tabs>

      {activeSection === "general" ? (
      <Paper variant="outlined" sx={{ p: 2, display: "grid", gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Create Invoice Options
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose which invoice creation options users can select when creating an invoice from a sales order.
          </Typography>
        </Box>

        {loading ? <CircularProgress size={24} /> : null}

        {CREATE_INVOICE_MODE_OPTIONS.map((option) => (
          <Box key={option.id} sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabledModes.includes(option.id)}
                  onChange={() => toggleMode(option.id)}
                  disabled={loading || saving}
                />
              }
              label={<Typography variant="subtitle2">{option.title}</Typography>}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
              {option.description}
            </Typography>
          </Box>
        ))}

        {!enabledModes.length ? (
          <Alert severity="error">Enable at least one invoice creation option.</Alert>
        ) : null}

        <FormControl fullWidth size="small" disabled={loading || saving || !enabledOptions.length}>
          <InputLabel id="default-invoice-creation-option-label">
            Default invoice creation option
          </InputLabel>
          <Select
            labelId="default-invoice-creation-option-label"
            label="Default invoice creation option"
            value={enabledModes.includes(defaultMode) ? defaultMode : enabledOptions[0]?.id || ""}
            onChange={(event) => setDefaultMode(event.target.value)}
          >
            {enabledOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ pt: 1, borderTop: "1px solid #e5e7eb" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Invoice Review Before Creation
          </Typography>
        </Box>
        <FormControlLabel
          control={(
            <Checkbox
              checked={enableInvoiceCreationReview}
              onChange={(_, checked) => setEnableInvoiceCreationReview(checked)}
              disabled={loading || saving}
            />
          )}
          label={(
            <Box>
              <Typography variant="body2">Enable invoice review and editing</Typography>
              <Typography variant="caption" color="text.secondary">
                Allows users to review and edit invoice contents before the invoice is created. When disabled, the standard invoice creation flow is used without the review/edit step.
              </Typography>
            </Box>
          )}
        />

        <Box sx={{ pt: 1, borderTop: "1px solid #e5e7eb", display: "grid", gap: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Invoice Email Sender
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This setting applies to customer invoice emails sent when an invoice is created
              and when an invoice is resent. It controls the From and Reply-To addresses.
            </Typography>
          </Box>

          <FormControl disabled={loading || saving}>
            <RadioGroup
              aria-label="Invoice email sender mode"
              value={invoiceEmailSender.mode}
              onChange={(event) => setInvoiceEmailSender((prev) => ({ ...prev, mode: event.target.value }))}
            >
              <FormControlLabel
                value={INVOICE_EMAIL_SENDER_MODES.USER_SELECTABLE}
                control={<Radio />}
                label="Allow sender selection when emailing an invoice"
              />
              <FormControlLabel
                value={INVOICE_EMAIL_SENDER_MODES.PREFERRED_USER}
                control={<Radio />}
                label="Always use a preferred finance or administrator user"
              />
              <FormControlLabel
                value={INVOICE_EMAIL_SENDER_MODES.CUSTOM_EMAIL}
                control={<Radio />}
                label="Always use a fixed email address"
              />
            </RadioGroup>
          </FormControl>

          {invoiceEmailSender.mode === INVOICE_EMAIL_SENDER_MODES.PREFERRED_USER ? (
            <Autocomplete
              options={invoiceSenderUserOptions}
              value={invoiceSenderUserOptions.find((option) => option.id === invoiceEmailSender.preferredUserId) || null}
              onChange={(_, option) => setInvoiceEmailSender((prev) => ({
                ...prev, preferredUserId: option?.id || null,
              }))}
              getOptionLabel={(option) => `${option.label} (${option.email})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={filterFinanceRecipientOptions}
              disabled={loading || saving || financeUsersLoading}
              ListboxProps={{ style: AUTOCOMPLETE_LISTBOX_STYLE }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Preferred invoice sender"
                  size="small"
                  required
                  helperText="Only this user's email can be used for invoice sends and resends."
                />
              )}
            />
          ) : null}

          {invoiceEmailSender.mode === INVOICE_EMAIL_SENDER_MODES.CUSTOM_EMAIL ? (
            <Box sx={{ display: "grid", gap: 1.25, maxWidth: 580 }}>
              <TextField
                label="Fixed invoice sender email"
                size="small"
                required
                value={invoiceEmailSender.customEmail}
                disabled={loading || saving}
                error={Boolean(invoiceEmailSender.customEmail) && !isValidInvoiceSenderEmail(invoiceEmailSender.customEmail)}
                helperText="This will be the only From and Reply-To address for all customer invoices in this instance."
                onChange={(event) => setInvoiceEmailSender((prev) => ({
                  ...prev, customEmail: event.target.value,
                }))}
              />
              <TextField
                label="Sender display name (optional)"
                size="small"
                value={invoiceEmailSender.customName}
                disabled={loading || saving}
                placeholder="e.g. Elite Promotional Marketing - Accounting"
                onChange={(event) => setInvoiceEmailSender((prev) => ({
                  ...prev, customName: event.target.value,
                }))}
              />
              <Alert severity="info">
                The address must be authenticated and approved as a sender in Brevo.
                The invoice composer will show it as read-only.
              </Alert>
            </Box>
          ) : null}

          {invoiceEmailSender.mode === INVOICE_EMAIL_SENDER_MODES.USER_SELECTABLE ? (
            <Typography variant="caption" color="text.secondary">
              The invoice email composer retains its existing finance/administrator From dropdown.
              Existing instances default to this behaviour until a different mode is saved.
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ pt: 1, borderTop: "1px solid #e5e7eb" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Bill Entry Options
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            When enabled, bills for selected internal decorators can be saved without vendor invoice details. This is intended for internal decoration departments where costs are tracked but no third-party vendor invoice is received.
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={billingConfig.allowDetailedItemTaxCustomization !== false}
              onChange={(_, checked) => setBillingConfig((prev) => ({
                ...prev,
                allowDetailedItemTaxCustomization: checked,
              }))}
              disabled={loading || saving}
            />
          }
          label={
            <Box>
              <Typography variant="body2">Allow detailed billing item tax customization</Typography>
              <Typography variant="caption" color="text.secondary">
                Allows item-level tax selection and tax override amounts. When disabled, item taxes continue to calculate automatically from the bill default tax.
              </Typography>
            </Box>
          }
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={billingConfig.minimalBillEntry.enabled}
              onChange={handleMinimalBillEntryEnabledChange}
              disabled={loading || saving}
            />
          }
          label="Enable minimal bill entry for internal decorators"
        />

        <FormControl fullWidth size="small" disabled={loading || saving || !decoratorOptions.length}>
          <InputLabel id="internal-decorators-label">Internal decorators</InputLabel>
          <Select
            labelId="internal-decorators-label"
            multiple
            value={selectedInternalDecoratorIds}
            onChange={handleInternalDecoratorIdsChange}
            label="Internal decorators"
            renderValue={(selected) => {
              const ids = Array.isArray(selected) ? selected.map((value) => Number(value)) : [];
              if (!ids.length) return "No internal decorators selected";
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {ids.map((id) => {
                    const option = decoratorOptions.find((decorator) => decorator.id === id);
                    return (
                      <Chip
                        key={id}
                        size="small"
                        label={option?.name || `Decorator ${id}`}
                      />
                    );
                  })}
                </Box>
              );
            }}
          >
            {decoratorOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox checked={selectedInternalDecoratorIds.includes(option.id)} />
                <ListItemText primary={option.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ pt: 1, display: "grid", gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Internal Suppliers
          </Typography>
          <SupplierSearchSelectAsync
            key={supplierSearchKey}
            instanceUniqueId={instanceUniqueId}
            value={null}
            tab="core"
            searchMode="core"
            label="Search suppliers"
            placeholder="Search suppliers..."
            onSelectSupplier={handleInternalSupplierSelect}
          />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Selected:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {selectedInternalSupplierIds.length ? selectedInternalSupplierIds.map((id) => {
                const supplier = internalSupplierOptions.find(
                  (option) => Number(option?.id ?? option?.Id) === Number(id)
                );
                const supplierName = supplier?.Name || supplier?.name || `Supplier #${id}`;
                return (
                  <Chip
                    key={id}
                    size="small"
                    label={supplierName}
                    onDelete={() => handleInternalSupplierRemove(id)}
                    disabled={loading || saving}
                  />
                );
              }) : (
                <Typography variant="caption" color="text.secondary">
                  No internal suppliers selected
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ pt: 1, borderTop: "1px solid #e5e7eb", display: "grid", gap: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Deposit Check Recipients
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose which finance or administrator users receive Deposit Check requests from the sales order page.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <Chip
              size="small"
              label={`${selectedEligibleRecipientCount} of ${financeUsersTotalCount} eligible recipients selected`}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                handleDepositCheckRecipientIdsChange([
                  ...invalidSelectedRecipientIds,
                  ...financeRecipientOptions.map((option) => option.id),
                ])
              }
              disabled={loading || saving || financeUsersLoading || !financeRecipientOptions.length}
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={() => handleDepositCheckRecipientIdsChange([])}
              disabled={loading || saving || !selectedRecipientIds.length}
            >
              Clear All
            </Button>
          </Box>

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={financeRecipientOptions}
            value={selectedFinanceRecipientOptions}
            loading={financeUsersLoading}
            disabled={loading || saving}
            filterOptions={filterFinanceRecipientOptions}
            ListboxProps={{
              "data-testid": "finance-recipient-listbox",
              style: AUTOCOMPLETE_LISTBOX_STYLE,
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, nextOptions) =>
              handleDepositCheckRecipientIdsChange([
                ...invalidSelectedRecipientIds,
                ...nextOptions.map((option) => option.id),
              ])
            }
            noOptionsText={
              financeUsersEmpty
                ? "No eligible finance or administrator users are available in this instance."
                : "No eligible recipients match your search."
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                <Box sx={{ display: "grid" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[option.email, formatRoleLabel(option.role)].filter(Boolean).join(" - ")}
                  </Typography>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  size="small"
                  label={option.label}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Deposit Check recipients"
                placeholder={
                  financeUsersLoading ? "Loading eligible recipients..." : "Search recipients"
                }
                helperText="Search by first name, last name, full name, email, or role."
              />
            )}
          />

          {financeUsersLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading eligible recipients...
            </Typography>
          ) : null}

          {financeUsersError ? (
            <Alert severity="warning">{financeUsersError}</Alert>
          ) : null}

          {financeUsersEmpty ? (
            <Alert severity="info">
              No eligible finance or administrator users are available in this instance.
            </Alert>
          ) : null}

          {invalidSelectedRecipientIds.length ? (
            <Alert severity="warning">
              Some saved Deposit Check recipients are no longer available or no longer eligible. Review them before saving.
            </Alert>
          ) : null}

          {invalidSelectedRecipientIds.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {invalidSelectedRecipientIds.map((id) => (
                <Chip
                  key={id}
                  size="small"
                  color="warning"
                  label={`Unavailable user #${id}`}
                  onDelete={() =>
                    handleDepositCheckRecipientIdsChange(
                      selectedRecipientIds.filter((selectedId) => selectedId !== id)
                    )
                  }
                />
              ))}
            </Box>
          ) : null}
        </Box>

        <Box sx={{ pt: 1, borderTop: "1px solid #e5e7eb", display: "grid", gap: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Sales Order Snapshot Updates
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose whether generated Sales Orders must be manually regenerated after cart
              changes, or are automatically updated when Sales Order cart changes are saved.
            </Typography>
          </Box>

          <FormControl disabled={loading || saving}>
            <RadioGroup
              value={salesOrderSnapshotConfig.updateMode}
              onChange={(event) =>
                setSalesOrderSnapshotConfig({ updateMode: event.target.value })
              }
            >
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1.5 }}>
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="Require manual Sales Order regeneration"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                  Cart changes are saved immediately, but users must click “Regenerate Sales
                  Order” before those changes appear in the generated Sales Order snapshot.
                </Typography>
              </Box>
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1.5, mt: 1 }}>
                <FormControlLabel
                  value="automatic"
                  control={<Radio />}
                  label="Automatically update Sales Order snapshot"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                  When a Sales Order cart change is successfully saved, Elite Core automatically
                  refreshes the current generated Sales Order snapshot.
                </Typography>
              </Box>
            </RadioGroup>
          </FormControl>

          <Alert severity="info">
            Automatic updates use the same Sales Order regeneration workflow as the existing
            “Regenerate Sales Order” action and run only after a cart change is saved.
          </Alert>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={save} disabled={loading || saving || !enabledModes.length}>
            {saving ? "Saving..." : "Save Financial Configuration"}
          </Button>
        </Box>
      </Paper>
      ) : null}

      {activeSection === "stripe" && showStripePayments ? (
        <Paper variant="outlined" sx={{ p: 2, display: "grid", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Stripe Payments</Typography>
            <Typography variant="body2" color="text.secondary">
              Customize the descriptions Elite Core sends to Stripe when payment sessions are created. These settings help Finance identify payments clearly in Stripe.
            </Typography>
          </Box>
          {stripeStatus?.usable ? <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip color="success" label="Active" />
            <Chip label={stripeStatus.mode === "test" ? "Test" : "Live"} />
            <Chip label={stripeStatus.paymentOwner === "instance"
              ? "Instance Stripe Account"
              : String(stripeStatus.paymentOwner || "platform").replace(/^./, (c) => c.toUpperCase())} />
          </Box> : null}
          {stripeStatus?.usable ? <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Elite Core Payments</Typography>
            <Typography variant="body2" color="text.secondary">
              Descriptions used by native Elite Core Stripe Checkout sessions.
            </Typography>
          </Box> : null}
          {stripeStatus?.usable ? <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Stripe Payment Descriptions</Typography>
            <Typography variant="caption" color="text.secondary">
              Available variables: {"{orderId} {invoiceId} {customerName} {amount} {currency} {paymentType}"}
            </Typography>
          </Box> : null}
          {stripeStatus?.usable ? stripeDescriptionFields.map(([type, label]) => (
            <Box key={type} sx={{ display: "grid", gap: 0.75 }}>
              <TextField
                label={label}
                value={stripeDescriptions[type]}
                disabled={loading || saving}
                onChange={(event) => setStripePaymentsConfig((previous) => ({
                  ...previous,
                  native: {
                    ...previous.native,
                    descriptions: { ...previous.native.descriptions, [type]: event.target.value },
                  },
                }))}
              />
              <Typography variant="caption" color="text.secondary">
                Preview: {resolveStripePaymentDescriptionPreview(stripeDescriptions[type], {
                  ...previewContext,
                  paymentType: {
                    invoice: "Invoice",
                    deposit: "Deposit",
                    balance: "Balance",
                    fullUpfront: "Full Upfront",
                    partial: "Partial",
                    progress: "Progress",
                  }[type],
                })}
              </Typography>
            </Box>
          )) : null}
          {stripeStatus?.usable ? <Typography variant="caption" color="text.secondary">
            Preview values — Project: 346 · Invoice: 123 · Customer: Example Company · Amount: 500.00 · Currency: CAD
          </Typography> : null}
          {stripeStatus?.hasStripeEnabledPromoStore ? (
            <Box sx={{ pt: 1, borderTop: "1px solid #e5e7eb", display: "grid", gap: 1 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>PromoStore Payments</Typography>
                <Typography variant="body2" color="text.secondary">
                  PromoStore payments are processed by WooCommerce. This value is synchronized to WordPress for the payment gateway to use when it creates Stripe payments.
                </Typography>
              </Box>
              <TextField
                label="Company default Stripe Payment Description"
                value={promoStoreDescription}
                disabled={loading || saving}
                onChange={(event) => setStripePaymentsConfig((previous) => ({
                  ...previous,
                  promoStores: {
                    descriptions: { payment: event.target.value },
                  },
                }))}
                helperText={'Available variables: {promoStoreName} {orderNumber} {wooOrderId} {customerName} {amount} {currency}'}
              />
              <Typography variant="caption" color="text.secondary">
                Preview: {String(promoStoreDescription)
                  .replaceAll("{promoStoreName}", "Example Store")
                  .replaceAll("{orderNumber}", "123")}
              </Typography>
            </Box>
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={save} disabled={loading || saving}>
              {saving ? "Saving..." : "Save Stripe Payment Descriptions"}
            </Button>
          </Box>
        </Paper>
      ) : null}

      {activeSection === "qbo" && showQboIdentifiers ? (
        <Paper variant="outlined" sx={{ p: 2, display: "grid", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>QuickBooks Online</Typography>
            <Typography variant="body2" color="text.secondary">
              Customize the transaction-level identifiers Elite Core sends to QuickBooks so Finance can identify invoices and vendor bills clearly.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip color={qboStatus?.connected ? "success" : "default"} label={qboStatus?.connected ? "Connected" : "Not connected"} />
            <Chip label={String(qboStatus?.env || "sandbox").toUpperCase()} />
          </Box>

          <Alert severity="info">
            Customer Memo may appear on the customer-facing QuickBooks document. Private Note is for internal QuickBooks accounting users only.
          </Alert>

          {["Customer Invoice", "Vendor Bill"].map((section) => (
            <Box key={section} sx={{ display: "grid", gap: 1.5, pt: 1, borderTop: "1px solid #e5e7eb" }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{section}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {section === "Customer Invoice"
                    ? "Transaction-level memo fields for QBO customer invoice exports."
                    : "Transaction-level private note for QBO vendor bill exports. Bill line descriptions are unchanged."}
                </Typography>
              </Box>

              {QBO_IDENTIFIER_TEMPLATE_FIELDS.filter((field) => field.section === section).map((field) => {
                const value = qboIdentifierTemplates[field.key] || "";
                const displayTemplate = value || DEFAULT_QBO_IDENTIFIER_TEMPLATES[field.key] || "";
                const unsupportedTokens = findUnsupportedQboIdentifierTokens(value, field.variables);
                return (
                  <Box key={field.key} sx={{ display: "grid", gap: 0.75 }}>
                    <TextField
                      label={field.label}
                      value={value}
                      disabled={loading || qboSaving}
                      placeholder={DEFAULT_QBO_IDENTIFIER_TEMPLATES[field.key]}
                      onChange={(event) => setQboIdentifierTemplates((previous) => ({
                        ...previous,
                        [field.key]: event.target.value,
                      }))}
                      helperText={field.description}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Available variables: {field.variables.map((token) => `{${token}}`).join(" ")}
                    </Typography>
                    {unsupportedTokens.length ? (
                      <Alert severity="warning">
                        Unsupported variable{unsupportedTokens.length === 1 ? "" : "s"}: {unsupportedTokens.map((token) => `{${token}}`).join(" ")}
                      </Alert>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      Preview: {resolveQboIdentifierPreview(displayTemplate, qboPreviewContext, field.variables)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}

          <Typography variant="caption" color="text.secondary">
            Blank fields preserve existing QuickBooks export behavior. Preview values are examples only and are never saved as transaction data.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={saveQboIdentifiers} disabled={loading || qboSaving}>
              {qboSaving ? "Saving..." : "Save QuickBooks Identifiers"}
            </Button>
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
}
